import { useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { ApiError, api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useToast } from "../components/Toast";
import { useSessionUser, useLogout } from "../lib/session";

/**
 * Change-password screen. Reached automatically (and exclusively) when an admin
 * has reset the user's password — `ProtectedRoute` forces every other route here
 * until the user sets their own password. Also usable as a voluntary change.
 */
export function ChangePassword() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const user = useSessionUser();
  const logout = useLogout();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  const forced = user.mustChangePassword;

  const change = useMutation({
    mutationFn: () => api.changePassword({ currentPassword, newPassword }),
    onSuccess: (updated) => {
      // Refresh the cached session (mustChangePassword now false) and continue.
      queryClient.setQueryData(["me"], updated);
      toast.success("Password berhasil diperbarui");
      navigate("/", { replace: true });
    },
    onError: (err) => {
      const m =
        err instanceof ApiError && err.status === 401
          ? "Password saat ini salah."
          : err instanceof Error
            ? err.message
            : "Gagal mengganti password";
      toast.error(m);
    },
  });

  const tooShort = newPassword.length > 0 && newPassword.length < 8;
  const mismatch = confirm.length > 0 && newPassword !== confirm;
  const sameAsOld =
    newPassword.length > 0 && newPassword === currentPassword;
  const canSubmit =
    currentPassword.length > 0 &&
    newPassword.length >= 8 &&
    newPassword === confirm &&
    newPassword !== currentPassword &&
    !change.isPending;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (canSubmit) change.mutate();
  };

  return (
    <div className="flex min-h-full items-center justify-center bg-slate-100 p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <div className="mb-1 text-2xl font-extrabold tracking-tight text-navy">
            AI Marketing<span className="text-teal">.</span>
          </div>
          <CardTitle>Atur password baru</CardTitle>
        </CardHeader>
        <CardContent>
          {forced && (
            <p className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
              Password Anda telah direset oleh admin. Demi keamanan, silakan atur
              password baru sebelum melanjutkan.
            </p>
          )}
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="current">Password saat ini</Label>
              <Input
                id="current"
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new">Password baru</Label>
              <Input
                id="new"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
              {tooShort && (
                <p className="text-xs text-rose-600">Minimal 8 karakter.</p>
              )}
              {sameAsOld && (
                <p className="text-xs text-rose-600">
                  Harus berbeda dari password saat ini.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm">Ulangi password baru</Label>
              <Input
                id="confirm"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
              />
              {mismatch && (
                <p className="text-xs text-rose-600">
                  Konfirmasi password tidak cocok.
                </p>
              )}
            </div>
            <Button
              type="submit"
              variant="primary"
              className="w-full"
              disabled={!canSubmit}
            >
              {change.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Simpan password"
              )}
            </Button>
          </form>
          <button
            type="button"
            onClick={() => logout.mutate()}
            className="mt-4 w-full text-center text-xs text-slate-500 hover:text-navy"
          >
            Keluar
          </button>
        </CardContent>
      </Card>
    </div>
  );
}
